// @flow
import { handleResponseOld, makeGet, makePost } from './helpers';

type FetchBidderDataParams = {
    authToken: string,
    deployment: string,
};

export const fetchBidderData = ({ authToken, deployment }: FetchBidderDataParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>auth/spauser' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type PostBidderConsentParams = {
    authToken: string,
    consent: any,
    context: any,
    deployment: string,
};

export const postBidderConsent = ({ authToken, consent, context, deployment }: PostBidderConsentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'accepttermsofservice', authToken, deployment, path: '<LEGACY-USER-API>' });
        request.send({ acceptedTermsOfService: consent, context, signUpClient: 'web' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type FetchUpcomingDataParams = {
    authToken: string,
    bidderId: number,
    deployment: string,
};

export const fetchUpcomingItemData = ({ authToken, bidderId, deployment }: FetchUpcomingDataParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: '<ITEM-API>upcoming-items/bidder',
            queryParams: { bidderId },
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type PostCompleteAccountParams = {
    authToken: string,
    bidderDetails: any,
    deployment: string,
};

export const postCompleteAccount = ({ authToken, bidderDetails, deployment }: PostCompleteAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: '<MAINHOST-API>api/u_bidr',
            queryParams: { client_version: '4.3' },
        });
        request.type('form');
        if (bidderDetails) {
            // why is there an if?

            const transformed: any = {
                addr1: bidderDetails.address1,
                addr2: bidderDetails.address2,
                city: bidderDetails.city,
                country: bidderDetails.country,
                fname: bidderDetails.firstName,
                lname: bidderDetails.lastName,
                phone: bidderDetails.mobile,
                postal_code: bidderDetails.zipcode,
                sms_phone: bidderDetails.mobile,
                state: bidderDetails.state,
                username: bidderDetails.username,
            };

            if (bidderDetails.email) {
                transformed.email_address = bidderDetails.email;
            }

            request.send(transformed);
        }
        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response,
                transform: details => {
                    const { auth_tokens, bidder, error_message, success, token } = details;

                    // Return the error package if there is an error.
                    if (!success) {
                        return {
                            error: true,
                            message: error_message,
                            success,
                        };
                    }

                    const {
                        address,
                        address2,
                        bidder_id,
                        city,
                        country,
                        join_date,
                        email,
                        firstName,
                        hasEverRegisteredForAuction,
                        lastName,
                        mobileNumber,
                        phoneNumber,
                        standing,
                        state,
                        stripe_customer,
                        username,
                        zip,
                        profile_complete,
                        zipCodeGeoCoordinates,
                    } = bidder;

                    return {
                        bidder: {
                            address,
                            address2,
                            bidderId: bidder_id,
                            city,
                            country,
                            createdDate: join_date,
                            creditCardValidated: false, //TODO: figure out why this data is not on the bidder obj from mainhost
                            email,
                            firstName,
                            hasEverRegisteredForAuction,
                            lastName,
                            mobileNumber,
                            phoneNumber,
                            profileComplete: Boolean(profile_complete),
                            standing,
                            state,
                            username,
                            zip,
                            zipCodeGeoCoordinates,
                        },
                        mainhostLogin: auth_tokens,
                        stripeCustomer: stripe_customer?.content || null,
                        token,
                    };
                },
            })
        );
    });

type PostCreateAccountParams = {
    agree: any,
    deployment: string,
    emailAddress: string,
};

export const postCreateAccount = ({ agree, deployment, emailAddress }: PostCreateAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'createuser', deployment, path: '<LEGACY-USER-API>' });
        request.send({
            acceptedTermsOfService: agree,
            client_version: '4.3',
            email_address: emailAddress,
            signup_client: 'web',
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type PostLoginParams = {
    deployment: string,
    mainhostLogin: any,
    password: ?string,
    username: string,
};

export const postLogin = ({ deployment, mainhostLogin, password, username }: PostLoginParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spalogin' });
        request.type('form');
        request.send({ password, username, ...mainhostLogin });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type PostSetPasswordParams = {
    authToken: string,
    deployment: string,
    password: string,
};

export const postSetPassword = ({ authToken, deployment, password }: PostSetPasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'createpassword', authToken, deployment, path: '<LEGACY-USER-API>' });
        request.send({ password });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

type PostSetSearchFacetOptionExclusionParams = {
    authToken: string,
    deployment: string,
    exclude: any,
    facetId: any,
    optionId: any,
};

export const postSetSearchFacetOptionExclusion = ({
    authToken,
    deployment,
    exclude,
    facetId,
    optionId,
}: PostSetSearchFacetOptionExclusionParams) =>
    new Promise<any>((resolve, reject) => {
        let requestURL;
        // transform the `exclude` bool into a number (0 or 1)
        const excludeBit = ~~exclude;

        switch (facetId) {
            case 'auctionHouse':
                requestURL = `<ITEM-API>search/exclusion/seller/${optionId}/${excludeBit}`;
                break;
            default:
                return reject(`Excluding search results by ${facetId} is not yet supported.`);
        }
        const request = makePost({ authToken, deployment, path: requestURL });
        request.send();
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
